import React from 'react';
import { Box, Chip } from '@mui/material';
import { grey, blue } from '@mui/material/colors';
import { CameraAlt } from '@mui/icons-material';
import { MUITableColumn } from 'components/shared/MUITable';
import { Stack, Typography, IconButton, Tooltip } from '@mui/material';
import { InventoryItemEntity } from './InventoryTable';
import { formatMoney } from '../../utils';
import { capitalizeFirstLetter } from 'formatters';
import { Edit } from '@mui/icons-material';
import { getAssetUrl } from 'utils/getAssetUrl';

// Account that uses attachment-only inventory, so blank equipment should read "Attachment".
const ATTACHMENT_FALLBACK_ACCOUNT_ID = '87eff2a0-e951-11ef-8fa8-231bf4435108';

export function createTableColumns({
  useHistory,
  accountId,
}: {
  useHistory: any;
  accountId?: string;
}): MUITableColumn<InventoryItemEntity>[] {
  const unknownEquipmentLabel = accountId === ATTACHMENT_FALLBACK_ACCOUNT_ID ? 'Attachment' : 'Unknown';

  return [
    {
      header: 'Photo',
      renderKey: 'assets',
      renderCell: ({ assets }) => {
        const firstImage = assets?.find(a => !a.type || a.type === 'IMAGE');
        if (firstImage) {
          return (
            <Box
              component="img"
              src={getAssetUrl(firstImage.source)}
              alt="Equipment"
              sx={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 1, display: 'block' }}
            />
          );
        }
        return (
          <Box
            sx={{
              width: 64,
              height: 48,
              borderRadius: 1,
              bgcolor: grey[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CameraAlt sx={{ color: grey[400], fontSize: 20 }} />
          </Box>
        );
      },
    },
    {
      header: 'Equipment',
      renderKey: 'equipment',
      renderCell: ({ year, make, model, description, vin, stockNumber }) => (
        <Stack
          sx={{
            width: '100%',
            alignItems: 'flex-start', // <-- this is the correct value
            textAlign: 'left', // <-- ensures Typography text is left-aligned
            borderRadius: '0.5rem',
          }}
        >
          {year || make || model ? (
            <Typography>{`${year ?? ''} ${make ?? ''} ${model ?? ''}`}</Typography>
          ) : (
            <Typography>{unknownEquipmentLabel}</Typography>
          )}

          <Typography variant="body2" color="text.secondary">
            {stockNumber ? `#${stockNumber}` : ''}
            {stockNumber && vin ? ' | ' : ''}
            {vin ? `VIN: ${vin}` : ''}
          </Typography>
        </Stack>
      ),
    },
    {
      header: 'Condition',
      renderKey: 'condition',
      sortKey: 'condition',
      renderCell: ({ condition }) => {
        const isNew = condition?.toLowerCase() === 'new';
        return (
          <Chip
            size="small"
            label={condition ? capitalizeFirstLetter(condition) : 'Used'}
            sx={{
              backgroundColor: isNew ? blue[100] : grey[200],
              color: isNew ? blue[800] : grey[700],
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      header: 'Mileage',
      renderKey: 'mileage',
      sortKey: 'mileage',
      renderCell: ({ mileage }) => <>{mileage || mileage === 0 ? mileage.toLocaleString() : '-'}</>,
    },
    {
      header: 'List Price',
      renderKey: 'retailPrice',
      sortKey: 'retail_price',
      renderCell: ({ retailPrice }) => <>{retailPrice ? `$${formatMoney(retailPrice, 0)}` : ''}</>,
    },
    {
      header: 'Status',
      renderKey: 'status',
      sortKey: 'status',
      renderCell: ({ status }) => <>{status ? capitalizeFirstLetter(status) : 'Unknown'}</>,
    },
    {
      header: 'Actions',
      renderKey: 'equipmentId',
      renderCell: ({ equipmentId }) => (
        <Tooltip title="Update Equipment">
          <IconButton
            size="small"
            color="primary"
            className="icon-btn-bordered"
            onClick={e => {
              e.stopPropagation();
              useHistory.push(`/equipment/${equipmentId}`);
            }}
          >
            <Edit />
          </IconButton>
        </Tooltip>
      ),
    },
  ];
}
